import Penpal, { IConnectionObject } from 'penpal';
import { CONFIG_PENPAL_CONNECTION_TIMEOUT } from '../../config';
import { PermissionCore } from '../interfaces';
import { PermissionModel, PermissionData, LayerParameters, AditionClientConfig } from '../../interfaces';
import { PermissionClientMessaging } from '../../client/tools/interfaces';
import {
  updatePermissionModel,
  mergePermissionModels,
  getPermissionsWithoutTimestamps
} from './permission-model.util';
import * as Logger from '../../common/tools/logger';
import { AditionService } from './adition.service';
import { PermissionLayerOptions } from '../../layer/interfaces';
import { Dimensions } from '../../client/interfaces';
import { PpsService } from './pps.service';

/**
 * @class
 * @name CommunicationHandlerService
 * @description Function to handle the communication between the components (Permission Layer, Core and Publisher Page).
 */
export class CommunicationHandlerService {

  /**
   * @var {string} moduleName Reflects the modules name.
   */
  private moduleName = `CommunicationHandlerService`;

  private aditionService: AditionService = new AditionService();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private parentConnection: IConnectionObject<any>;

  /**
   * Permission Client (parent) API exposed through Penpal
   */
  private permissionClient: PermissionClientMessaging;

  private parentApi: PermissionCore = {
    initializePermissionLayer: async (permissionModel: PermissionModel, settings) => {
      Logger.info(this.moduleName, `initializePermissionLayer`, 'parent called initializePermissionLayer', permissionModel);

      this.permissionModel = permissionModel;
      if (settings && (settings.accessToken || settings.touchpointName || settings.touchpointReference || settings.xUIApp)) {
        try {
          this.ppsService = new PpsService({
            accessToken: settings.accessToken,
            touchpointName: settings.touchpointName,
            touchpointReference: settings.touchpointReference,
            xUIApp: settings.xUIApp
          });

          await this.syncModels();
        } catch (error) {
          Logger.error(this.moduleName, 'initializePermissionLayer', error);
        }
      }

      if (this.permissionClient) {
        await this.aditionService.init(this.permissionModel, this);
      } else {
        // Workaround for SWAT-2135
        const handle = window.setInterval(async () => {
          if (this.permissionClient) {
            window.clearInterval(handle);
            await this.aditionService.init(this.permissionModel, this);
          }
        }, 100);
      }

    }
  };

  private ppsService: PpsService;

  /**
   * The currently active set of permissions. This will be updated as the user grants/revokes permissions
   */
  public permissionModel: PermissionModel;

  /**
   * @public
   * @async
   * @function initialize
   * @description Function to initialize a post message listener to handle commands.
   * @return {Promise<void>}
   */
  public async initialize(): Promise<void> {

    this.parentConnection = Penpal.connectToParent({
      // Methods child is exposing to parent
      methods: this.parentApi,
      timeout: CONFIG_PENPAL_CONNECTION_TIMEOUT,
    });
    this.permissionClient = await this.parentConnection.promise as PermissionClientMessaging;

    Logger.debug(this.moduleName, `initialize`, `Module initialized.`);
  }

  /**
   * @public
   * @function openPermissionLayer
   * @description Forwards the command from the Permission Layer (Adition Iframe) to the Permission Client (Integration Library)
   * @param {PermissionLayerOptions} options
   * @return {void}
   */
  public openPermissionLayer(options?: PermissionLayerOptions): void {
    return this.permissionClient.openPermissionLayer(options);
  }

  /**
   * @public
   * @function closePermissionLayer
   * @description Forwards the command from the Permission Layer (Adition Iframe) to the Permission Client (Integration Library)
   * @return {void}
   */
  public async closePermissionLayer(): Promise<void> {
    if (this.ppsService) {
      await this.syncModels();
    }

    if (this.permissionClient) {
      this.permissionClient.closePermissionLayer();
    }

    this.destroy();
  }

  /**
   * @public
   * @function setPermissions
   * @description Forwards the command from the Permission Layer (Adition Iframe) to the Permission Client (Integration Library)
   * @param {PermissionData[]} permissionData
   * @return {void}
   */
  public async setPermissions(permissionData: PermissionData[]): Promise<void> {
    Logger.info(this.moduleName, 'setPermissions', 'Received updated permissions from layer', JSON.stringify(permissionData, null, 2));
    permissionData.forEach((permission) => {
      /* istanbul ignore next else */
      if (permission.usecase && permission.status) {
        this.permissionModel = updatePermissionModel(this.permissionModel, permission);
      }
    });

    if (this.ppsService) {
      try {
        Logger.info(this.moduleName, 'setPermissions', `Storing ${permissionData.length} updated permissions in PPS`, JSON.stringify(permissionData,null, 2));
        // TODO: additional requests for other identity types (SWAT-2329)
        await this.ppsService.writePermissions(permissionData);
      } catch(err) {
        return Promise.reject(`${this.moduleName} setPermissions: Error while writing permissions: ${err}`);
      }
    }

    this.permissionClient.setPermissions(this.permissionModel);
  }

  /**
   * @public
   * @function updatePermissionLayer
   * @description Forwards the command from the Permission Layer (Adition Iframe) to the Permission Client (Integration Library)
   * @return {void}
   */
  public updatePermissionLayer(): void {
    this.aditionService.init(this.permissionModel, this);
  }

  /**
   * @public
   * @function updatePermissionLayer
   * @description Forwards the command from the Permission Layer (Adition Iframe) to the Permission Client (Integration Library)
   * @param {Dimensions} dimensions
   * @return {void}
   */
  public resizePermissionLayer(dimensions: Dimensions): void {
    this.permissionClient.resizePermissionLayer(dimensions);
  }

  /**
   * @public
   * @async
   * @function getAditionClientConfig
   * @description Function to get the required ADITION config, set by the Permission Client.
   * @return {Promise<AditionClientConfig>}
   */
  public async getAditionClientConfig(): Promise<AditionClientConfig> {
    return this.permissionClient.getAditionClientConfig();
  }

  /**
   * @public
   * @async
   * @description Function to get custom consent parameters from the publisher page.
   * @return {Promise<LayerParameters>}
   */
  public async getLayerParameters(): Promise<LayerParameters> {
    return this.permissionClient.getParameters();
  }

  /**
   * @public
   * @async
   * @function getIdentity
   * @description Get users identity from client
   * @return {Promise<string>}
   */
  public async getIdentity(): Promise<string> {
    return this.permissionClient.getIdentity();
  }

  /**
   * Synchronize the local model with remote data from the permission service(s)
   */
  protected async syncModels(): Promise<void> {
    let remoteData: PermissionData[];
    Logger.debug(this.moduleName, 'syncModels', 'Fetching remote permissions...');
    try {
      // TODO: additional requests for other identity types; merge the response payloads (SWAT-2329)
      remoteData = await this.ppsService.readPermissions();
    } catch (ex) {
      Logger.error(this.moduleName, 'syncModels', `Error while fetching remote model:`, ex);
      return;
    }

    if (!remoteData) {
      Logger.error(this.moduleName, 'syncModels', `No remote model after sync!`);
      return;
    }

    // ignore permissions with missing usecase names. This has been fixed in PPS but can still occur for older permission data
    remoteData = remoteData.filter((remotePerm) => !!remotePerm.usecase);

    Logger.info(this.moduleName, 'syncModels', `Found ${remoteData.length} remote permissions:`, JSON.stringify(remoteData, null, 2));

    this.permissionModel = mergePermissionModels(this.permissionModel, remoteData);
    Logger.debug(this.moduleName, 'syncModels', 'local model after sync:', JSON.stringify(this.permissionModel, null, 2));
    this.permissionClient.setPermissions(this.permissionModel);

    const newPermissions = getPermissionsWithoutTimestamps(this.permissionModel.data);
    /* istanbul ignore next else */
    if (newPermissions.length > 0 && this.ppsService) {
      try {
        // TODO: additional requests for other identity types (SWAT-2329)
        await this.ppsService.writePermissions(newPermissions);
      } catch(err) {
        return Promise.reject(`${this.moduleName} setPermissions: Error while writing permissions: ${err}`);
      }
    }
  }

  /**
   * @function destroy
   * @description Function to exit correctly
   * @return {void}
   */
  public destroy(): void {
    if (this.parentConnection) {
      this.parentConnection.destroy();
      this.parentConnection = null;
    }

    /* istanbul ignore else */
    if (this.aditionService) {
      this.aditionService.destroy();
      this.aditionService = null;
    }

    this.permissionClient = null;
    this.permissionModel = null;
  }

}
